import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Menu, Share2, Eye, Trash2, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { getFormattedRecentlyViewed, removeFromRecentlyViewed } from '../../services/recentlyViewedService';
import { useDispatch } from 'react-redux';
import { toggleWishlist } from '../../features/wishlist/wishlistSlice';
import { addToWishlist as addToWishlistAPI, removeFromWishlist as removeFromWishlistAPI } from '../../services/wishlistService';
import api from '../../services/api';
import CarCard from './CarCard';
import { useToast } from '../../contexts/ToastContext';
import '../AllCss/RecentlyViewedCars.css';

// Allowed statuses for recently viewed cars (constant array, no dependencies needed)
const ALLOWED_STATUSES = ['available', 'on-hold', 'purchase-in-progress', 'sold', 'delivered'];

function RecentlyViewedCars() {
  const toast = useToast();
  const allCars = useSelector((state) => state.cars.items || []);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [openMenuId, setOpenMenuId] = useState(null);
  const dispatch = useDispatch();
  const wishlistIds = useSelector((state) => state.wishlist.items);
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  
  // Calculate responsive cards per view
  const getCardsPerView = () => {
    if (typeof window === 'undefined') return 4;
    if (window.innerWidth < 480) return 1; // Mobile: 1 card
    if (window.innerWidth < 1024) return 3; // Tablet: 3 cards
    return 4; // Desktop: 4 cards
  };
  
  const [cardsPerView, setCardsPerView] = useState(getCardsPerView());
  const containerRef = useRef(null);

  // Fetch recently viewed cars from API
  const fetchRecentlyViewedFromAPI = useCallback(async () => {
    if (!isAuthenticated) {
      // Fallback to localStorage if not authenticated
      const viewed = getFormattedRecentlyViewed(20);
      setRecentlyViewed(viewed);
      return;
    }

    try {
      // Add timestamp to bypass browser cache
      const response = await api.get('/recently-viewed', {
        params: { 
          limit: 20, // Show all 20 recently viewed cars (matches MAX_ITEMS in server)
          _t: Date.now() // Timestamp to prevent caching
        },
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.data && response.data.success && response.data.data?.recentlyViewed) {
        const apiData = response.data.data.recentlyViewed;
        setRecentlyViewed(apiData);
      } else {
        // If response structure is unexpected, log and fallback
        console.warn('⚠️ Unexpected response structure:', response.data);
        const viewed = getFormattedRecentlyViewed(20);
        setRecentlyViewed(viewed);
      }
    } catch (error) {
      // If API fails, fallback to localStorage
      console.error('Error fetching recently viewed from API:', error);
      const viewed = getFormattedRecentlyViewed(20);
      setRecentlyViewed(viewed);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Initial load
    fetchRecentlyViewedFromAPI();

    // Set up interval to fetch every 2 seconds
    const interval = setInterval(() => {
      fetchRecentlyViewedFromAPI();
    }, 2000); // 2 seconds = 2000ms

    // Listen for updates when new cars are viewed (localStorage fallback)
    const handleStorageChange = () => {
      if (!isAuthenticated) {
        const viewed = getFormattedRecentlyViewed(20);
        setRecentlyViewed(viewed);
      }
    };

    // Listen for custom event when a car is viewed
    window.addEventListener('recentlyViewedUpdated', handleStorageChange);
    
    // Also listen for storage events (in case of multiple tabs)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('recentlyViewedUpdated', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchRecentlyViewedFromAPI, isAuthenticated]);

  // Handle window resize for responsive cards per view
  useEffect(() => {
    const handleResize = () => {
      setCardsPerView(getCardsPerView());
      // Reset to first slide if necessary
      setCurrentIndex(0);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Process recently viewed cars - use API data directly, fallback to matching with Redux
  const recentlyViewedCars = useMemo(() => {
    if (!recentlyViewed || recentlyViewed.length === 0) {
      return [];
    }

    return recentlyViewed
      .map(viewedItem => {
        // If API provides full car data, use it directly
        // Otherwise try to match with allCars from Redux
        if (viewedItem.id && viewedItem.make && viewedItem.model) {
          // API already provides full car data, use it directly
          return viewedItem;
        } else {
          // Fallback: try to find in Redux allCars
        const fullCar = allCars.find(car => 
          String(car.id) === String(viewedItem.id) || 
          String(car.stockNumber) === String(viewedItem.stockNumber)
        );
        if (fullCar) {
          return {
            ...fullCar,
            viewedDate: viewedItem.viewedDate,
            viewedAt: viewedItem.viewedAt,
          };
          }
        }
        return null;
      })
      .filter(car => car !== null)
      .filter(car => {
        // Only show cars with allowed statuses
        const carStatus = car.status?.toLowerCase();
        return ALLOWED_STATUSES.includes(carStatus);
      });
      // No slice limit - show all filtered cars (up to 20 from API)
  }, [recentlyViewed, allCars]);

  // Calculate total slides for the carousel
  const totalSlides = Math.max(1, Math.ceil(recentlyViewedCars.length / cardsPerView));

  // Scroll to slide when indicator is clicked
  const goToSlide = (index) => {
    if (containerRef.current) {
      const container = containerRef.current;
      const cardWidth = container.offsetWidth / cardsPerView;
      container.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setCurrentIndex(index);
    }
  };

  // Navigate to previous slide
  const goToPrevious = () => {
    if (currentIndex > 0) {
      goToSlide(currentIndex - 1);
    }
  };

  // Navigate to next slide
  const goToNext = () => {
    if (currentIndex < totalSlides - 1) {
      goToSlide(currentIndex + 1);
    }
  };

  // Get status badge info
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'sold':
        return { label: 'Sold', className: 'status-sold' };
      case 'on-hold':
        return { label: 'On Hold', className: 'status-on-hold' };
      case 'purchase-in-progress':
        return { label: 'Purchase In Progress', className: 'status-purchase-in-progress' };
      case 'delivered':
        return { label: 'Delivered', className: 'status-delivered' };
      case 'available':
      default:
        return null; // Don't show badge for 'available' status
    }
  };

  const handleRemoveFromRecentlyViewed = (carId) => {
    removeFromRecentlyViewed(carId);
    const viewed = getFormattedRecentlyViewed(20);
    setRecentlyViewed(viewed);
    setOpenMenuId(null);
    window.dispatchEvent(new CustomEvent('recentlyViewedUpdated'));
  };

  const handleShare = (car) => {
    const url = `${window.location.origin}/cars/${car.stockNumber || car.id}`;
    if (navigator.share) {
      navigator.share({
        title: `${car.year} ${car.make} ${car.model}`,
        text: `Check out this ${car.year} ${car.make} ${car.model}`,
        url: url,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => {
        toast.success('Link copied to clipboard!');
      }).catch(() => {});
    }
    setOpenMenuId(null);
  };

  const handleToggleWishlist = async (car) => {
    const carIdString = String(car.id);
    const isWishlisted = wishlistIds.some(id => String(id) === carIdString);
    
    try {
      if (isWishlisted) {
        await removeFromWishlistAPI(car.id);
      } else {
        await addToWishlistAPI(car.id);
      }
      dispatch(toggleWishlist(car.id));
    } catch (error) {
      console.error('Error toggling wishlist:', error);
    }
    setOpenMenuId(null);
  };

  const toggleMenu = (carId) => {
    setOpenMenuId(openMenuId === carId ? null : carId);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.recently-viewed-menu')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);


  if (recentlyViewedCars.length === 0) {
    return null;
  }

  return (
    <div className="recently-viewed-section-simple">
      <div className="recently-viewed-header">
        <h4>Your recently viewed</h4>
      </div>
      
      <div className="recently-viewed-carousel-wrapper">
        {/* Left Arrow */}
        {totalSlides > 1 && (
          <button
            className="recently-viewed-carousel-arrow recently-viewed-carousel-arrow-left"
            onClick={goToPrevious}
            disabled={currentIndex === 0}
            aria-label="Previous slide"
          >
            <ChevronLeft size={24} />
          </button>
        )}

        <div 
          className="recently-viewed-carousel-container"
          ref={containerRef}
          onScroll={(e) => {
            const container = e.target;
            const scrollLeft = container.scrollLeft;
            const cardWidth = container.offsetWidth / cardsPerView;
            const newIndex = Math.round(scrollLeft / cardWidth);
            if (newIndex !== currentIndex && newIndex >= 0 && newIndex < totalSlides) {
              setCurrentIndex(newIndex);
            }
          }}
        >
        <div className="recently-viewed-carousel-track">
        {recentlyViewedCars.map((car) => {
          const carIdString = String(car.id);
          const isWishlisted = wishlistIds.some(id => String(id) === carIdString);
          const statusBadge = getStatusBadge(car.status);
          return (
            <div key={car.id} className="recently-viewed-carousel-slide">
              <div className="recently-viewed-card-item">
              <div className="recently-viewed-card-wrapper">
                {/* Status Badge - Show for non-available statuses */}
                {statusBadge && (
                  <div className={`recently-viewed-status-badge ${statusBadge.className}`}>
                    {statusBadge.label}
                  </div>
                )}
                <div className="recently-viewed-card-content">
                    <CarCard car={car} />
                  
                  {/* Menu Button - Positioned next to title */}
                  <div className="recently-viewed-title-menu-wrapper">
                    <div className="recently-viewed-title-with-menu">
                        <div className="recently-viewed-title-spacer"></div>
                        <div className="recently-viewed-menu-button-wrapper">
                        <button
                          className="recently-viewed-menu-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleMenu(car.id);
                          }}
                          aria-label="More options"
                        >
                          <Menu size={18} />
                        </button>
                        
                        {openMenuId === car.id && (
                          <div className="recently-viewed-menu-dropdown recently-viewed-menu">
                              <button
                              className="menu-item"
                                onClick={() => handleToggleWishlist(car)}
                            >
                                <Heart size={16} fill={isWishlisted ? 'currentColor' : 'none'} />
                                <span>{isWishlisted ? 'Remove from favorites' : 'Add to favorites'}</span>
                              </button>
                            <button
                              className="menu-item"
                              onClick={() => handleShare(car)}
                            >
                              <Share2 size={16} />
                              <span>Share</span>
                            </button>
                              <Link
                                to={`/cars/${car.stockNumber || car.id}`}
                                className="menu-item"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <Eye size={16} />
                                <span>Features & specs</span>
                              </Link>
                            <button
                              className="menu-item menu-item-danger"
                              onClick={() => handleRemoveFromRecentlyViewed(car.id)}
                            >
                              <Trash2 size={16} />
                                <span>Remove</span>
                            </button>
                          </div>
                        )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </div>

        {/* Right Arrow */}
        {totalSlides > 1 && (
          <button
            className="recently-viewed-carousel-arrow recently-viewed-carousel-arrow-right"
            onClick={goToNext}
            disabled={currentIndex >= totalSlides - 1}
            aria-label="Next slide"
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>

      {/* Indicators */}
      {totalSlides > 1 && (
        <div className="recently-viewed-carousel-indicators">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              className={`recently-viewed-carousel-indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default RecentlyViewedCars;

